import { Helmet } from "react-helmet-async";
import { isFidolia, validateUrl, ensureAbsoluteUrl, getDefaultLogo } from "utils";
import PropTypes from 'prop-types';

function SEO({ seo }) {
  if (!seo) return null;
  
  const {
    title,
    siteName = isFidolia() ? "Fidolia" : "Mybe",
    description,
    logo,
    icon,
    type = "OnlineStore",
    keywords = [],
    robots = "index, follow",
    noindex = false,
    canonical: customCanonical,
    locale = "fr_FR",
    structuredData,
    social = {},
  } = seo;

  // Validate and prepare URLs
  const safeCanonical = customCanonical 
    ? ensureAbsoluteUrl(customCanonical) 
    : window.location.href;
  
  const safeLogo = validateUrl(logo) || getDefaultLogo(isFidolia());
  const safeIcon = validateUrl(icon) || safeLogo;
  
  // pageTitle: just the shop name (shown as <title> and og:title)
  const pageTitle = title && title.trim().length > 0 ? title : siteName;
  // siteDisplayName: "Mybe · Shop name" (shown above URL in Google via og:site_name)
  const siteDisplayName =
    title && title.trim().length > 0 ? `${siteName} · ${title}` : siteName;
  // Prepare description (with fallback)
  const safeDescription = description || title || 
    `Bienvenue sur ${title} - Votre destination en ligne pour des produits et services de qualité`;
  
  // Prepare meta robots
  const safeRobots = noindex ? "noindex, nofollow" : robots;
  
  // Generate JSON-LD structured data
  const generateStructuredData = () => {
    if (structuredData) return structuredData;
    
    const baseData = {
      "@context": "https://schema.org",
      "@type": getSchemaType(type),
      "name": siteName,
      "url": safeCanonical,
      "inLanguage": locale,
    };
    
    // Add logo if available
    if (safeLogo) {
      baseData.logo = {
        "@type": "ImageObject",
        "url": safeLogo,
        "width": "512",
        "height": "512",
        "caption": `${siteName} Logo`
      };
      baseData.image = safeLogo;
    }
    
    // Add description
    if (safeDescription) {
      baseData.description = safeDescription;
    }
    
    // Add additional properties based on type
    switch(type) {
      case 'OnlineStore':
        baseData.potentialAction = {
          "@type": "SearchAction",
          "target": `${window.location.origin}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        };
        break;
      case 'Article':
      case 'BlogPosting':
        baseData.publisher = {
          "@type": "Organization",
          "name": title || siteName,
          "logo": safeLogo ? {
            "@type": "ImageObject",
            "url": safeLogo
          } : undefined
        };
        break;
        default:
        baseData.potentialAction = {
          "@type": "SearchAction",
          "target": `${window.location.origin}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        };
        break;
    }
    
    return baseData;
  };

  const jsonLdData = generateStructuredData();
if (!title && !description) return null;
  return (
    <Helmet>
      {/* Title */}
      <title>{pageTitle}</title>
      
      {/* Basic Meta Tags */}
      <meta name="description" content={safeDescription} />
      <meta name="robots" content={safeRobots} />
      
      {/* Keywords (optional) */}
      {keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Viewport for mobile */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={safeCanonical} />
      
      {/* Favicon */}
      <link rel="icon" href={safeIcon} type={getMimeType(safeIcon)} />
      <link rel="apple-touch-icon" href={safeIcon} />
      
      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={safeDescription} />
      <meta property="og:type" content={getOpenGraphType(type)} />
      <meta property="og:url" content={safeCanonical} />
      <meta property="og:site_name" content={siteDisplayName} />
      <meta property="og:locale" content={locale} />
      
      {/* Open Graph Image */}
      {safeLogo && (
        <>
          <meta property="og:image" content={safeLogo} />
          <meta property="og:image:alt" content={`${title || siteName} Logo`} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      )}
      
      {/* Additional Open Graph Tags from social prop */}
      {social.openGraph && Object.entries(social.openGraph).map(([key, value]) => (
        <meta key={key} property={`og:${key}`} content={value} />
      ))}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content={safeLogo ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={safeDescription} />
      {social.twitterSite && (
        <meta name="twitter:site" content={social.twitterSite} />
      )}
      {social.twitterCreator && (
        <meta name="twitter:creator" content={social.twitterCreator} />
      )}
      
      {/* Twitter Image */}
      {safeLogo && (
        <meta name="twitter:image" content={safeLogo} />
      )}
      
      {/* Additional Twitter Tags from social prop */}
      {social.twitter && Object.entries(social.twitter).map(([key, value]) => (
        <meta key={key} name={`twitter:${key}`} content={value} />
      ))}
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLdData)}
      </script>
      
      {/* Additional structured data if provided */}
      {social.additionalSchema && (
        <script type="application/ld+json">
          {JSON.stringify(social.additionalSchema)}
        </script>
      )}
    </Helmet>
  );
}

// Helper functions (add these to your utils)
const getSchemaType = (type) => {
  const typeMap = {
    'OnlineStore': 'OnlineStore',
    'Store': 'OnlineStore',
    'Website': 'WebSite',
    'Article': 'Article',
    'Blog': 'Blog',
    'Product': 'Product',
    'Service': 'Service',
    'Organization': 'Organization',
    'LocalBusiness': 'LocalBusiness',
    'Person': 'Person',
    'Event': 'Event',
  };
  return typeMap[type] || type;
};

const getOpenGraphType = (type) => {
  const ogTypeMap = {
    'OnlineStore': 'website',
    'Store': 'website',
    'Website': 'website',
    'Article': 'article',
    'Blog': 'article',
    'Product': 'product',
    'Service': 'website',
    'Organization': 'website',
    'LocalBusiness': 'website',
    'Person': 'profile',
    'Event': 'event',
  };
  return ogTypeMap[type] || 'website';
};

const getMimeType = (url) => {
  if (!url) return 'image/x-icon';
  const extension = url.split('.').pop().toLowerCase();
  const mimeTypes = {
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'ico': 'image/x-icon',
    'webp': 'image/webp'
  };
  return mimeTypes[extension] || 'image/x-icon';
};

// Add PropTypes for better development
SEO.propTypes = {
  seo: PropTypes.shape({
    title: PropTypes.string,
    siteName: PropTypes.string,
    description: PropTypes.string,
    logo: PropTypes.string,
    icon: PropTypes.string,
    type: PropTypes.string,
    keywords: PropTypes.arrayOf(PropTypes.string),
    robots: PropTypes.string,
    noindex: PropTypes.bool,
    canonical: PropTypes.string,
    locale: PropTypes.string,
    structuredData: PropTypes.object,
    social: PropTypes.shape({
      openGraph: PropTypes.object,
      twitter: PropTypes.object,
      twitterSite: PropTypes.string,
      twitterCreator: PropTypes.string,
      additionalSchema: PropTypes.object,
    }),
  }),
};

export default SEO;